// @ts-nocheck
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import sanitizeTagCategories from 'helpers/sanitizeTagCategories'

import Button from 'js/components/Button'
import CustomTagsField from 'js/components/CustomTagsField'
import Errors from 'js/components/Errors'
import Flexer from 'js/components/Flexer/Flexer'
import FormItem from 'js/components/Form/FormItem'
import Modal from 'js/components/Modal/Modal'
import Setting from 'js/components/Setting'
import Spacer from 'js/components/Spacer/Spacer'
import TagCategoriesField from 'js/components/TagCategories/TagCategoriesField'
import TitleSlugField from 'js/page/AddEditForm/components/TitleSlugField'
import useCustomTags from 'js/page/Widget/hooks/useCustomTags'

import PageGuidField from './PageGuidField'

const SettingsModal = ({ site, viewer, entity = {}, isVisible, onClose }) => {
    const { t } = useTranslation()

    const [editPage] = useMutation<any>(EDIT_PAGE)

    const [mutationErrors, setMutationErrors] = useState()

    const {
        guid,
        group,
        title,
        urlSlug,
        tags,
        tagCategories,
        parent,
        richDescription,
        isRecommendedInSearch,
        showNavigation,
    } = entity

    const [categoryTags, setCategoryTags] = useState(tagCategories || [])

    const { customTags, handleAddTags, handleRemoveTags } = useCustomTags(tags)

    const [containerGuid, setContainerGuid] = useState(parent?.guid)

    const defaultValues = {
        title,
        urlSlug,
        pageType: null,
        richDescription,
        isRecommendedInSearch: isRecommendedInSearch || false,
        showNavigation: showNavigation || false,
    }

    const formMethods = useForm({
        mode: 'onChange',
        defaultValues,
    })

    const {
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
    } = formMethods

    const submit = async ({
        title,
        urlSlug,
        richDescription,
        accessId,
        isRecommendedInSearch,
        showNavigation,
    }) => {
        const input = {
            containerGuid: containerGuid || null,
            guid,
            title,
            ...(urlSlugEnabled ? { urlSlug } : {}),
            richDescription,
            accessId: parseInt(accessId, 10),
            tagCategories: sanitizeTagCategories(
                site.tagCategories,
                categoryTags,
            ),
            tags: Array.from(customTags),
            isRecommendedInSearch,
            showNavigation,
        }

        setMutationErrors()

        await editPage({
            variables: {
                input,
            },
            refetchQueries: ['PageList', 'PageEdit'],
        })
            .then(() => {
                onClose()
            })
            .catch((errors) => {
                console.error(errors)
                setMutationErrors(errors)
            })
    }

    const onError = (errors) => {
        return new Promise((resolve, reject) => {
            reject(new Error(errors))
        })
    }

    const urlSlugEnabled = site.editUrlSlugEnabled && !group

    return (
        <Modal
            isVisible={isVisible}
            onClose={onClose}
            title={t('global.settings')}
            size="normal"
        >
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(submit, onError)}>
                    <Spacer>
                        <TitleSlugField
                            entity={entity}
                            urlSlugEnabled={urlSlugEnabled}
                        />
                        <FormItem
                            control={control}
                            type="rich"
                            name="richDescription"
                            title={t('form.description')}
                            options={{
                                textFormat: true,
                                textStyle: true,
                                textLink: true,
                                textQuote: true,
                                textList: true,
                                insertMedia: true,
                                insertAccordion: true,
                                insertTable: true,
                            }}
                            errors={errors}
                        />
                        <TagCategoriesField
                            name="filters"
                            tagCategories={site.tagCategories}
                            value={categoryTags}
                            setTags={setCategoryTags}
                        />
                        {site.customTagsAllowed && (
                            <CustomTagsField
                                name="customTags"
                                value={customTags}
                                onAddTag={handleAddTags}
                                onRemoveTag={handleRemoveTags}
                            />
                        )}

                        {!group && (
                            <PageGuidField
                                guid={guid}
                                value={containerGuid}
                                onChange={setContainerGuid}
                            />
                        )}

                        {!containerGuid && (
                            <Setting
                                subtitle="global.navigation"
                                helper="page.navigation-helper"
                                htmlFor="showNavigation"
                            >
                                <FormItem
                                    control={control}
                                    type="switch"
                                    name="showNavigation"
                                    size="small"
                                />
                            </Setting>
                        )}

                        {viewer?.isAdmin && (
                            <Setting
                                subtitle="form.recommended-search-result"
                                helper="form.recommended-search-result-helper"
                                htmlFor="isRecommendedInSearch"
                            >
                                <FormItem
                                    control={control}
                                    type="switch"
                                    name="isRecommendedInSearch"
                                    size="small"
                                />
                            </Setting>
                        )}
                    </Spacer>

                    <Errors errors={mutationErrors} />

                    <Flexer mt>
                        <Button
                            size="normal"
                            variant="tertiary"
                            onClick={onClose}
                        >
                            {t('action.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            size="normal"
                            variant="primary"
                            disabled={!isValid}
                            loading={isSubmitting}
                        >
                            {t('action.save')}
                        </Button>
                    </Flexer>
                </form>
            </FormProvider>
        </Modal>
    )
}

const EDIT_PAGE = gql`
    mutation EditPage($input: editPageInput!) {
        editPage(input: $input) {
            entity {
                guid
                ... on Page {
                    title
                    timeUpdated
                }
            }
        }
    }
`

export default SettingsModal
